// Admin feedback inbox (slice 28, ADR 0027). Owner-facing list of
// customer feedback for one outlet, with a detail panel for reply +
// state triage. Server-side permission gating is authoritative
// (feedback:read for list, feedback:respond for writes); the UI
// hides controls staff can't use so they never see a button that
// 403s on click.

import { useEffect, useState, type FormEvent, type JSX } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiError } from '../api/client.js';
import type {
  FeedbackDetailResponse,
  FeedbackListResponse,
  FeedbackRow,
  FeedbackState,
  MeResponse,
  RespondFeedbackBody,
  RespondFeedbackResponse,
  UpdateFeedbackStateBody,
  UpdateFeedbackStateResponse,
} from '../api/types.js';
import { useAuth } from '../auth/AuthContext.js';
import { hasPermission } from '../auth/permissions.js';

type FilterState = FeedbackState | 'all';
const FILTERS: readonly { value: FilterState; label: string }[] = [
  { value: 'new', label: 'New' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'responded', label: 'Responded' },
  { value: 'archived', label: 'Archived' },
  { value: 'all', label: 'All' },
];

export function FeedbackInboxPage(): JSX.Element {
  const { orgId, outletId } = useParams<{ orgId: string; outletId: string }>();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFilter = (searchParams.get('state') as FilterState | null) ?? 'new';
  const [filter, setFilter] = useState<FilterState>(initialFilter);
  const highlightId = searchParams.get('id');
  const [selectedId, setSelectedId] = useState<string | null>(highlightId);

  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => apiFetch<MeResponse>('/me'),
    staleTime: 30_000,
  });

  const canRespond = (() => {
    if (!me.data || !outletId) return false;
    const membership = me.data.memberships.find((m) => m.outlet.id === outletId);
    return membership ? hasPermission(membership.permissions, 'feedback:respond') : false;
  })();

  const listKey = ['feedback-list', orgId, outletId, filter] as const;
  const list = useQuery({
    queryKey: listKey,
    queryFn: () => {
      const qs = filter === 'all' ? '' : `?state=${filter}`;
      return apiFetch<FeedbackListResponse>(
        `/organizations/${orgId}/outlets/${outletId}/feedback${qs}`,
      );
    },
    enabled: !!orgId && !!outletId,
  });

  useEffect(() => {
    // Keep ?state in the URL so a reload lands on the same filter.
    const sp = new URLSearchParams(searchParams);
    if (filter === 'new') sp.delete('state');
    else sp.set('state', filter);
    setSearchParams(sp, { replace: true });
  }, [filter, searchParams, setSearchParams]);

  return (
    <main className="page">
      <header className="page-header">
        <div>
          <h1>Feedback</h1>
          <p className="muted">
            Signed in as <strong>{user?.email}</strong> ·{' '}
            <Link to={`/orgs/${orgId}/outlets/${outletId}/menus`}>Back to menus</Link>
          </p>
        </div>
      </header>

      <section className="card">
        <div className="row filter-row" role="tablist" aria-label="Filter feedback by state">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              role="tab"
              aria-selected={filter === f.value}
              className={`chip ${filter === f.value ? 'chip-active' : ''}`}
              onClick={() => setFilter(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>

        {list.isLoading ? <p>Loading…</p> : null}
        {list.error ? <p className="error">Could not load feedback.</p> : null}
        {list.data && list.data.feedback.length === 0 ? (
          <p className="muted">No feedback in this view yet.</p>
        ) : null}

        {list.data && list.data.feedback.length > 0 ? (
          <ul className="feedback-list">
            {list.data.feedback.map((row) => (
              <FeedbackRowView
                key={row.id}
                row={row}
                highlighted={row.id === highlightId}
                selected={row.id === selectedId}
                onSelect={() => setSelectedId((cur) => (cur === row.id ? null : row.id))}
              />
            ))}
          </ul>
        ) : null}
      </section>

      {selectedId ? (
        <DetailPanel
          orgId={orgId!}
          feedbackId={selectedId}
          canRespond={canRespond}
          onAfterWrite={() => {
            void qc.invalidateQueries({ queryKey: ['feedback-list', orgId, outletId] });
          }}
        />
      ) : null}
    </main>
  );
}

interface RowProps {
  readonly row: FeedbackRow;
  readonly highlighted: boolean;
  readonly selected: boolean;
  readonly onSelect: () => void;
}

function FeedbackRowView({ row, highlighted, selected, onSelect }: RowProps): JSX.Element {
  const customerLabel =
    row.customer === null
      ? 'Anonymous'
      : [row.customer.displayName, row.customer.phoneMasked].filter(Boolean).join(' · ') ||
        'Customer';
  return (
    <li
      className={`feedback-row ${highlighted ? 'highlighted' : ''} ${selected ? 'selected' : ''}`}
    >
      <button
        type="button"
        className="feedback-row-button"
        onClick={onSelect}
        aria-expanded={selected}
      >
        <div className="feedback-row-main">
          <p className="feedback-row-top">
            <span className="rating" aria-label={`Rating ${row.rating} out of 5`}>
              {'★'.repeat(row.rating)}
              {'☆'.repeat(5 - row.rating)}
            </span>
            <span className="muted"> · {customerLabel}</span>
          </p>
          {row.comment ? <p className="feedback-comment">{row.comment}</p> : null}
          <p className="muted feedback-row-meta">
            {formatRelative(row.createdAt)} · state: {row.state}
            {row.googleTapthroughAt ? ' · tapped-through' : ''}
          </p>
        </div>
      </button>
    </li>
  );
}

interface DetailProps {
  readonly orgId: string;
  readonly feedbackId: string;
  readonly canRespond: boolean;
  readonly onAfterWrite: () => void;
}

function DetailPanel({ orgId, feedbackId, canRespond, onAfterWrite }: DetailProps): JSX.Element {
  const detailKey = ['feedback-detail', orgId, feedbackId] as const;
  const qc = useQueryClient();
  const detail = useQuery({
    queryKey: detailKey,
    queryFn: () =>
      apiFetch<FeedbackDetailResponse>(`/organizations/${orgId}/feedback/${feedbackId}`),
  });

  const [replyText, setReplyText] = useState('');

  const respond = useMutation({
    mutationFn: (text: string) =>
      apiFetch<RespondFeedbackResponse>(`/organizations/${orgId}/feedback/${feedbackId}/response`, {
        method: 'POST',
        body: { channel: 'in_app', text } satisfies RespondFeedbackBody,
      }),
    onSuccess: () => {
      setReplyText('');
      void qc.invalidateQueries({ queryKey: detailKey });
      onAfterWrite();
    },
  });

  const changeState = useMutation({
    mutationFn: (state: 'reviewed' | 'archived') =>
      apiFetch<UpdateFeedbackStateResponse>(
        `/organizations/${orgId}/feedback/${feedbackId}/state`,
        {
          method: 'POST',
          body: { state } satisfies UpdateFeedbackStateBody,
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: detailKey });
      onAfterWrite();
    },
  });

  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    const trimmed = replyText.trim();
    if (trimmed.length === 0) return;
    respond.mutate(trimmed);
  }

  if (detail.isLoading) return <section className="card">Loading…</section>;
  if (detail.error || !detail.data) {
    return (
      <section className="card">
        <p className="error">Could not load this feedback.</p>
      </section>
    );
  }

  const f = detail.data.feedback;
  const promptAnswerEntries = Object.entries(f.promptAnswers);

  return (
    <section className="card feedback-detail">
      <h2>Feedback detail</h2>
      {promptAnswerEntries.length > 0 ? (
        <dl className="prompt-answers">
          {promptAnswerEntries.map(([k, v]) => (
            <div key={k}>
              <dt>{k}</dt>
              <dd>{String(v)}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {f.comment ? (
        <blockquote className="feedback-comment-detail">{f.comment}</blockquote>
      ) : (
        <p className="muted">(no written comment)</p>
      )}

      <h3>Responses</h3>
      {detail.data.responses.length === 0 ? (
        <p className="muted">No responses yet.</p>
      ) : (
        <ul className="response-list">
          {detail.data.responses.map((r) => (
            <li key={r.id} className="response-row">
              <p className="response-body">{r.body}</p>
              <p className="muted response-meta">
                via {r.channel} · {formatRelative(r.createdAt)}
              </p>
            </li>
          ))}
        </ul>
      )}

      {canRespond ? (
        <>
          <form onSubmit={onSubmit} className="response-form">
            <label htmlFor="reply">Write a response</label>
            <textarea
              id="reply"
              rows={3}
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              disabled={respond.isPending || f.state === 'archived'}
              placeholder={
                f.state === 'archived'
                  ? 'Un-archive before responding.'
                  : 'Thanks for the feedback — we…'
              }
            />
            <div className="row-actions">
              <button
                type="submit"
                disabled={
                  respond.isPending || replyText.trim().length === 0 || f.state === 'archived'
                }
              >
                {respond.isPending ? 'Sending…' : 'Send response'}
              </button>
              {f.state !== 'reviewed' && f.state !== 'responded' ? (
                <button
                  type="button"
                  className="button secondary"
                  onClick={() => changeState.mutate('reviewed')}
                  disabled={changeState.isPending}
                >
                  Mark reviewed
                </button>
              ) : null}
              {f.state !== 'archived' ? (
                <button
                  type="button"
                  className="button secondary"
                  onClick={() => changeState.mutate('archived')}
                  disabled={changeState.isPending}
                >
                  Archive
                </button>
              ) : (
                <button
                  type="button"
                  className="button secondary"
                  onClick={() => changeState.mutate('reviewed')}
                  disabled={changeState.isPending}
                >
                  Un-archive
                </button>
              )}
            </div>
            {respond.error ? (
              <p className="error" role="alert">
                {mutationErrorMessage(respond.error, 'Could not send the response.')}
              </p>
            ) : null}
            {changeState.error ? (
              <p className="error" role="alert">
                {mutationErrorMessage(changeState.error, 'Could not change the state.')}
              </p>
            ) : null}
          </form>
        </>
      ) : (
        <p className="muted">You can view feedback but not respond.</p>
      )}
    </section>
  );
}

function mutationErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    if (error.status === 403) return 'You do not have permission to do that.';
    if (error.status === 404) return 'This feedback no longer exists.';
    if (error.status === 400) {
      const body = error.body as { message?: string; details?: { message: string }[] } | undefined;
      return body?.details?.[0]?.message ?? body?.message ?? fallback;
    }
    if (error.status === 0) return 'Network error — check your connection.';
  }
  return fallback;
}

function formatRelative(iso: string): string {
  const diff = Date.now() - Date.parse(iso);
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.round(diff / 60_000)} min ago`;
  if (diff < 86_400_000) return `${Math.round(diff / 3_600_000)} h ago`;
  return new Date(iso).toLocaleDateString();
}
